import { Component, OnDestroy, OnInit } from "@angular/core";
import { NavigationCancel, NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { LayoutService } from "../../core/layout.service";
import { MenuComponent } from "../../../kt/components";
import { ILayout, LayoutType } from "../../core/configs/config";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  private unsubscribe: Subscription[] = [];
  // Public props
  currentLayoutType: LayoutType | null;

  appHeaderDisplay: boolean;
  appHeaderDefaultFixedDesktop: boolean;
  appHeaderDefaultFixedMobile: boolean;

  appHeaderDefaultContainer: "fixed" | "fluid";
  headerContainerCssClass: string = "";
  appHeaderDefaultContainerClass: string = "";

  appHeaderDefaultStacked: boolean;

  // view
  appSidebarDefaultCollapseDesktopEnabled: boolean;
  appSidebarDisplay: boolean;
  appHeaderDefaultContent: string = "";
  appHeaderDefaulMenuDisplay: boolean;
  appPageTitleDisplay: boolean;

  /**
   * True while a quiz is actually being sat.
   *
   * A paper is the one screen where the site's own navigation is a hazard: six
   * menu items, a cart and an avatar sit above a half-answered question, and
   * every one of them throws the answers away. The 2021 build swapped in a
   * stripped header for exactly this; here the same header just stands down —
   * the nav and the cart go, one way out stays.
   *
   * Matches the take route only. `/online-class/quiz` (the list) and
   * `/online-class/quiz/result/12` keep the full header.
   */
  focusMode = false;
  private static readonly TAKE_ROUTE = /^\/online-class\/quiz\/\d+(?:[?#]|$)/;

  constructor(private layout: LayoutService, private router: Router) {
    this.focusMode = HeaderComponent.TAKE_ROUTE.test(this.router.url);
    this.routingChanges();
  }

  updateProps(config: ILayout) {
    this.appHeaderDisplay = this.layout.getProp(
      "app.header.display",
      config
    ) as boolean;
    // view
    this.appSidebarDefaultCollapseDesktopEnabled = this.layout.getProp(
      "app.sidebar.default.collapse.desktop.enabled",
      config
    ) as boolean;
    this.appSidebarDisplay = this.layout.getProp(
      "app.sidebar.display",
      config
    ) as boolean;
    this.appHeaderDefaultContent = this.layout.getProp(
      "app.header.default.content",
      config
    ) as string;
    this.appHeaderDefaulMenuDisplay = this.layout.getProp(
      "app.header.default.menu.display",
      config
    ) as boolean;
    this.appPageTitleDisplay = this.layout.getProp(
      "app.pageTitle.display",
      config
    ) as boolean;

    // body attrs and container css classes
    this.appHeaderDefaultFixedDesktop = this.layout.getProp(
      "app.header.default.fixed.desktop",
      config
    ) as boolean;
    if (this.appHeaderDefaultFixedDesktop) {
      document.body.setAttribute("data-kt-app-header-fixed", "true");
    }

    this.appHeaderDefaultFixedMobile = this.layout.getProp(
      "app.header.default.fixed.mobile",
      config
    ) as boolean;
    if (this.appHeaderDefaultFixedMobile) {
      document.body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    }

    this.appHeaderDefaultContainer = this.layout.getProp(
      "app.header.default.container",
      config
    ) as "fixed" | "fluid";
    this.headerContainerCssClass =
      this.appHeaderDefaultContainer === "fixed"
        ? "container-xl"
        : "container-fluid";

    this.appHeaderDefaultContainerClass = this.layout.getProp(
      "app.header.default.containerClass",
      config
    ) as string;
    if (this.appHeaderDefaultContainerClass) {
      this.headerContainerCssClass += ` ${this.appHeaderDefaultContainerClass}`;
    }

    this.appHeaderDefaultStacked = this.layout.getProp(
      "app.header.default.stacked",
      config
    ) as boolean;
    if (this.appHeaderDefaultStacked) {
      document.body.setAttribute("data-kt-app-header-stacked", "true");
    }

    // Primary header
    // Secondary header
  }

  ngOnInit(): void {
    const subscr = this.layout.layoutConfigSubject
      .asObservable()
      .subscribe((config: ILayout) => {
        this.updateProps(config);
      });
    this.unsubscribe.push(subscr);
    const layoutSubscr = this.layout.currentLayoutTypeSubject
      .asObservable()
      .subscribe((layout) => {
        this.currentLayoutType = layout;
      });
    this.unsubscribe.push(layoutSubscr);
  }

  routingChanges() {
    const routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
        this.focusMode = HeaderComponent.TAKE_ROUTE.test(this.router.url);
        MenuComponent.reinitialization();
      }
    });
    this.unsubscribe.push(routerSubscription);
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
